import { useState } from "react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

export default function JWTDecoder() {
  const [token, setToken] = useState("");
  const [decodedHeader, setDecodedHeader] = useState("");
  const [decodedPayload, setDecodedPayload] = useState("");
  const [error, setError] = useState("");

  const decodeJWT = () => {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) throw new Error("Invalid JWT format");

      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));

      setDecodedHeader(JSON.stringify(header, null, 2));
      setDecodedPayload(JSON.stringify(payload, null, 2));
      setError("");
    } catch (e) {
      setDecodedHeader("");
      setDecodedPayload("");
      setError(e.message || "Invalid JWT");
    }
  };

  const copyDecoded = () => {
    const combined = `Header:\n${decodedHeader}\n\nPayload:\n${decodedPayload}`;
    navigator.clipboard.writeText(combined);
    alert("Decoded JWT copied!");
  };

  return (
    <>
      <SEO
        title="JWT Decoder - Online Tool"
        description="Decode JWT tokens instantly online. View header, payload, and expiration."
        keywords="JWT decoder, JSON Web Token decode, online JWT tool, JWT validator"
        url="https://online-tools-center.vercel.app/jwt-decoder"
      />

      <Navbar />

      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-lg border-0 rounded-4 p-4">
              {/* HERO */}
              <div className="text-center mb-4">
                <h1 className="fw-bold mb-2">JWT Decoder</h1>
                <p className="text-muted">
                  Decode JWT tokens instantly online. View header, payload, and check expiration.
                </p>
              </div>

              <textarea
                rows="6"
                className="form-control mb-3"
                placeholder="Paste JWT token here"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />

              <div className="d-flex gap-2 mb-3">
                <button className="btn btn-primary" onClick={decodeJWT}>
                  Decode JWT
                </button>
                <button
                  className="btn btn-dark"
                  onClick={copyDecoded}
                  disabled={!decodedPayload && !decodedHeader}
                >
                  Copy Decoded
                </button>
              </div>

              {error && (
                <div className="alert alert-danger">{error}</div>
              )}

              {decodedHeader && decodedPayload && (
                <div className="mt-3">
                  <div className="mb-3">
                    <h5 className="fw-semibold">Header:</h5>
                    <pre
                      className="border rounded-3 p-3 bg-light"
                      style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
                    >
                      {decodedHeader}
                    </pre>
                  </div>

                  <div className="mb-3">
                    <h5 className="fw-semibold">Payload:</h5>
                    <pre
                      className="border rounded-3 p-3 bg-light"
                      style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
                    >
                      {decodedPayload}
                    </pre>
                  </div>
                </div>
              )}

              {/* SEO Content */}
             {/* SEO Content */}
<div className="mt-5">

  <h2 className="fw-bold mb-3">What is a JWT Token?</h2>
  <p>
    JWT stands for JSON Web Token. It is an open standard (RFC 7519) for
    securely transmitting information between two parties — typically a client
    and a server — as a compact, URL-safe string. JWTs are most commonly used
    for authentication and authorization in web applications and APIs. When a
    user logs into an application, the server generates a JWT and sends it to
    the client. The client then includes this token in the header of subsequent
    requests to prove its identity without needing to send credentials again.
    JWTs are widely used in modern web development with frameworks like Node.js,
    Django, Spring Boot, and ASP.NET, and are the standard token format for
    OAuth 2.0 and OpenID Connect authentication flows.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Structure of a JWT Token</h2>
  <p>
    A JWT token consists of three parts separated by dots: the Header, the
    Payload, and the Signature. Each part is Base64URL encoded, which is why
    a JWT always looks like a long string of random characters in three
    sections. The Header contains metadata about the token, typically specifying
    the token type (JWT) and the signing algorithm used such as HS256 (HMAC
    with SHA-256) or RS256 (RSA with SHA-256). The Payload contains the actual
    data called claims — these include standard fields like sub (subject, usually
    a user ID), iat (issued at timestamp), exp (expiration timestamp), and any
    custom fields the application includes such as user roles or email address.
    The Signature is a cryptographic hash created by combining the encoded
    header and payload with a secret key, used to verify that the token has
    not been tampered with.
  </p>

  <h2 className="fw-bold mt-5 mb-3">How to Use This JWT Decoder</h2>
  <ol>
    <li className="mb-2">
      <strong>Copy your JWT token</strong> from your application, browser
      developer tools, API response, or authentication logs. A JWT typically
      starts with "eyJ" and has two dots separating its three parts.
    </li>
    <li className="mb-2">
      <strong>Paste the token</strong> into the text area above.
    </li>
    <li className="mb-2">
      <strong>Click Decode JWT</strong> — the tool instantly decodes and
      displays the Header and Payload sections in formatted, readable JSON.
    </li>
    <li className="mb-2">
      <strong>Review the payload</strong> to inspect user ID, roles, email,
      token expiration time (exp), and any other claims stored in the token.
    </li>
    <li className="mb-2">
      <strong>Copy the decoded output</strong> using the Copy Decoded button
      if you need to save or share the decoded content.
    </li>
  </ol>

  <h2 className="fw-bold mt-5 mb-3">What Can You See When You Decode a JWT?</h2>
  <p>
    When you decode a JWT, the Header reveals which algorithm was used to sign
    the token — commonly HS256, RS256, or ES256. The Payload reveals all the
    claims embedded in the token. Standard claims include iss (issuer — which
    application or service issued the token), sub (subject — usually the user's
    unique ID), aud (audience — which service the token is intended for), exp
    (expiration time as a Unix timestamp), iat (issued at time), and nbf (not
    before — the earliest time the token is valid). Custom claims added by
    developers might include the user's email address, role (admin, user,
    moderator), permissions, or any other application-specific data. Checking
    the exp field is particularly useful for debugging authentication errors
    caused by expired tokens.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Important: JWT Decoding vs JWT Verification</h2>
  <p>
    It is important to understand that decoding a JWT and verifying a JWT are
    two different things. Decoding simply reads the Base64URL-encoded content
    of the header and payload — anyone can decode a JWT without any secret key
    because the content is encoded, not encrypted. Verification, on the other
    hand, checks the cryptographic signature using the secret key or public key
    to confirm that the token was genuinely issued by the expected server and
    has not been modified. Our decoder decodes the token for inspection purposes
    but does not verify the signature. Never use a decoded token as proof of
    authenticity — signature verification must always be done on your server
    using a JWT library.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Frequently Asked Questions</h2>

  <div className="mb-4">
    <h5>Is it safe to paste my JWT token here?</h5>
    <p>
      Yes. All decoding is performed entirely in your browser using JavaScript.
      Your JWT token is never sent to any server, logged, or stored anywhere.
      However, as a general security practice, avoid sharing JWTs that contain
      sensitive user data in public or shared environments, and never paste
      production tokens into online tools if your organization's security policy
      prohibits it.
    </p>
  </div>

  <div className="mb-4">
    <h5>Can I decode any JWT token?</h5>
    <p>
      Yes, you can decode any standard JWT token regardless of which algorithm
      was used to sign it, because decoding only reads the Base64URL content
      rather than verifying the signature. However, if a JWT uses encryption
      (JWE — JSON Web Encryption) rather than just signing, the payload will
      be encrypted and cannot be decoded without the private key. Most JWTs
      used in web applications are signed (JWS) rather than encrypted and can
      be decoded with this tool.
    </p>
  </div>

  <div className="mb-4">
    <h5>How do I check if a JWT token is expired?</h5>
    <p>
      After decoding the token, look for the exp field in the Payload section.
      This value is a Unix timestamp representing when the token expires. You
      can convert this to a readable date by using an epoch converter, or simply
      compare it to the current Unix timestamp. If the exp value is less than
      the current time, the token has expired and will be rejected by the server.
    </p>
  </div>

  <div className="mb-4">
    <h5>Does this tool verify the JWT signature?</h5>
    <p>
      No. This tool decodes the JWT to display its contents but does not verify
      the cryptographic signature. Signature verification requires the secret
      key (for HMAC algorithms) or the public key (for RSA/ECDSA algorithms)
      that was used to sign the token, which only the issuing server should
      possess. For security purposes, always perform signature verification
      server-side using a trusted JWT library in your programming language.
    </p>
  </div>

  {/* Related Tools */}
  <div className="mt-5 p-4 bg-light rounded-4">
    <h3 className="fw-bold mb-3">Related Developer Tools</h3>
    <div className="d-flex flex-wrap gap-3">
      <a href="/json-formatter" className="btn btn-outline-primary">JSON Formatter</a>
      <a href="/password-generator" className="btn btn-outline-primary">Password Generator</a>
      <a href="/word-counter" className="btn btn-outline-primary">Word Counter</a>
    </div>
  </div>

</div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}